import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { BackendService } from '../../core/backend.service';
import { MaintenanceAnalysisUserAcceptanceService } from '../../runnable-models/reliability-centered-maintenance/maintenance-analysis-details/for-user-acceptance/maintenance-analysis-user-acceptance.service';
import { EquipmentFailureEffect } from '../../viewmodels/equipment/equipment-failure-effect';
import { EquipmentFailureMode } from '../../viewmodels/equipment/equipment-failure-mode';
import { EquipmentFunction } from '../../viewmodels/equipment/equipment-function';
import { EquipmentFunctionInitialisation } from '../../viewmodels/equipment/equipment-function-initialisation';
import { EquipmentFunctionModelSelectionCriteria } from '../../viewmodels/equipment/equipment-function-model-selection-criteria';
import { EquipmentFunctionModelSelectionCriteriaBase } from '../../viewmodels/equipment/equipment-function-model-selection-criteria-base';
import { EquipmentFunctionalFailure } from '../../viewmodels/equipment/equipment-functional-failure';
import { FailureCause } from '../../viewmodels/equipment/failure-cause';
import { FailureMechanism } from '../../viewmodels/equipment/failure-mechanism';
import { FailureMechanismReason } from '../../viewmodels/equipment/failure-mechanism-reason';
import { FailureModesForPlannedMaintenance } from '../../viewmodels/equipment/failure-modes-for-planned-maintenance';
import { ModelFunctions } from '../../viewmodels/equipment/model-equipment-functions';
import { PlannedMaintenance } from '../../viewmodels/equipment/planned-maintenance';
import { CommercialImpactsService } from '../commercial-impacts/commercial-impacts.service';
import { IsoHierarchyQueryBaseService } from '../equipment-functions/iso-hierarchy-query-base.service';

@Injectable({
  providedIn: 'root',
})
export class ModelFunctionsService extends IsoHierarchyQueryBaseService {
  public modelFunctions: BehaviorSubject<ModelFunctions> = new BehaviorSubject<ModelFunctions>(undefined);
  public selectedFunction: BehaviorSubject<EquipmentFunction> = new BehaviorSubject<EquipmentFunction>(undefined);
  public selectedFunctionalFailure: BehaviorSubject<EquipmentFunctionalFailure> = new BehaviorSubject<EquipmentFunctionalFailure>(undefined);
  public selectedFailureMode: BehaviorSubject<EquipmentFailureMode> = new BehaviorSubject<EquipmentFailureMode>(undefined);
  public selectedFailureMechanism: BehaviorSubject<FailureMechanism> = new BehaviorSubject<FailureMechanism>(undefined);
  public selectedFailureMechanismReason: BehaviorSubject<FailureMechanismReason> = new BehaviorSubject<FailureMechanismReason>(undefined);
  public selectedFailureEffect: BehaviorSubject<EquipmentFailureEffect> = new BehaviorSubject<EquipmentFailureEffect>(undefined);
  public selectedFailureCause: BehaviorSubject<FailureCause> = new BehaviorSubject<FailureCause>(undefined);
  public onSaveRequested: Subject<any> = new Subject<any>();
  public failureModePatternChanged: Subject<EquipmentFailureMode> = new Subject<EquipmentFailureMode>();
  public plannedMaintenanceActivityChanged: Subject<PlannedMaintenance> = new Subject<PlannedMaintenance>();
  public plannedMaintenanceReferenceChanged: Subject<PlannedMaintenance> = new Subject<PlannedMaintenance>();
  public failureModesForPlannedMaintenanceChanged: Subject<FailureModesForPlannedMaintenance> = new Subject<FailureModesForPlannedMaintenance>();
  public failureModeCharacteristicChanged: Subject<EquipmentFailureMode> = new Subject<EquipmentFailureMode>();
  public failureEffectConsequenceChanged: Subject<EquipmentFailureMode> = new Subject<EquipmentFailureMode>();
  public failureModeCreated: Subject<EquipmentFailureMode> = new Subject<EquipmentFailureMode>();
  public loadFromModelRequested = new Subject();
  public importFunctionsRequested = new Subject<EquipmentFunction[]>();
  public plannedMaintenanceDeleted = new Subject();

  constructor(
    private backendService: BackendService,
    userAcceptanceService: MaintenanceAnalysisUserAcceptanceService,
    private commercialImpactService: CommercialImpactsService,
  ) {
    super();

    this.failureModeCreated.subscribe(failureMode => {
      userAcceptanceService.addUserAcceptanceRecord(failureMode, null);
    });
  }

  public initialise() {
    this.selectedFunction.next(undefined);
    this.selectedFunctionalFailure.next(undefined);
    this.selectedFailureMode.next(undefined);
    this.selectedFailureMechanism.next(undefined);
    this.selectedFailureMechanismReason.next(undefined);
    this.selectedFailureEffect.next(undefined);
    this.selectedFailureCause.next(undefined);
  }

  public getFailureTotal(failureEffect: EquipmentFailureEffect): number {
    if (!failureEffect || !failureEffect.commercialImpacts) {
      return 0;
    }

    return this.commercialImpactService.calculateCurrentTotal(failureEffect.commercialImpacts);
  }

  public loadFromModel() {
    this.loadFromModelRequested.next();
  }

  public createCriteria(): EquipmentFunctionModelSelectionCriteriaBase {
    const criteria = new EquipmentFunctionModelSelectionCriteria();
    criteria.requiresFunctions = true;
    criteria.excludedModelIds = this.excludedDataIds;

    return criteria;
  }

  public findData(criteria: EquipmentFunctionModelSelectionCriteria): Observable<EquipmentFunctionModelSelectionCriteriaBase> {
    criteria.searchResult = null;
    return this.backendService.create(EquipmentFunctionModelSelectionCriteria, criteria, false);
  }

  public selectData(criteria: EquipmentFunctionModelSelectionCriteria) {
    if (!criteria.hasModel()) {
      return;
    }

    this.importFunctionsRequested.next(criteria.searchResult.functions);
  }

  public clearFunctions(): void {
    if (this.modelFunctions.value) {
      this.modelFunctions.value.functions.length = 0;
    }
  }

  public importFunctions(modelId: string, newFunctions: EquipmentFunction[]) {
    newFunctions.forEach(func => {
      func.modelId = modelId;
      this.modelFunctions.value.functions.push(func);
    });
  }

  public getFunctions(criteria: EquipmentFunctionInitialisation): Observable<EquipmentFunctionInitialisation> {
    return this.backendService.create(EquipmentFunctionInitialisation, criteria, false);
  }
}
